/**
 * Application header (64px, sticky) per STANDARDSv2 §Layout.
 *
 * Element order is a platform contract, not a preference:
 *   [Grid menu] → [App logo/title] → [Primary nav] → … → [Notifications] → [User menu]
 *
 * The grid menu is the **true first element** — nothing sits to its left, including the mobile
 * hamburger, which is why the hamburger renders *after* it (verification item 22).
 */

import { Menu, Transition } from '@headlessui/react';
import {
  ArrowRightOnRectangleIcon,
  Bars3Icon,
  MagnifyingGlassIcon,
  BellIcon,
  ComputerDesktopIcon,
  MoonIcon,
  QuestionMarkCircleIcon,
  SunIcon,
  UserCircleIcon,
} from '@heroicons/react/24/outline';
import { Fragment } from 'react';
import { Link } from 'react-router-dom';

import { GridMenu } from './GridMenu';
import { useAuth } from '@/contexts/AuthContext';
import { useTheme } from '@/contexts/ThemeContext';

interface AppHeaderProps {
  onToggleSidebar: () => void;
  onOpenPalette: () => void;
}

export function AppHeader({ onToggleSidebar, onOpenPalette }: AppHeaderProps) {
  const { user, logout, isLocalAdmin } = useAuth();
  const { mode, cycleMode } = useTheme();

  const ThemeIcon = mode === 'light' ? SunIcon : mode === 'dark' ? MoonIcon : ComputerDesktopIcon;

  return (
    <header className="h-16 shrink-0 flex items-center gap-2 px-4 bg-white dark:bg-surface-900 border-b border-surface-200 dark:border-surface-700">
      {/*
        FIRST element in the header — mandatory placement. A break-glass session has no Bedrock
        identity, so the switcher is hidden rather than rendered broken.
      */}
      {user?.bedrock_available ? <GridMenu currentSlug={user.app_slug} /> : null}

      {/* Mobile sidebar toggle sits to the RIGHT of the grid menu, never to its left. */}
      <button
        type="button"
        onClick={onToggleSidebar}
        aria-label="Toggle navigation"
        className="lg:hidden p-2 rounded-lg text-surface-500 hover:bg-surface-100 dark:text-surface-400 dark:hover:bg-surface-800 transition-colors"
      >
        <Bars3Icon className="h-5 w-5" aria-hidden="true" />
      </button>

      <Link to="/" className="flex items-center gap-2 min-w-0">
        <span className="text-base font-semibold text-surface-900 dark:text-surface-100 truncate">
          {user?.app_name ?? 'Citara Central'}
        </span>
      </Link>

      <div className="flex-1" />

      {/*
        A visible entry point for the palette. The shortcut alone is undiscoverable, and the
        hint doubles as the place people learn Ctrl-K exists.
      */}
      <button
        type="button"
        onClick={onOpenPalette}
        aria-label="Search (Ctrl or Command K)"
        className="hidden sm:flex items-center gap-2 rounded-lg border border-surface-300 px-3 py-1.5 text-sm text-surface-500 hover:bg-surface-100 dark:border-surface-600 dark:hover:bg-surface-800 transition-colors"
      >
        <MagnifyingGlassIcon className="h-4 w-4" aria-hidden="true" />
        <span>Search</span>
        <kbd className="rounded border border-surface-300 bg-surface-100 px-1.5 font-mono text-xs dark:border-surface-600 dark:bg-surface-900">
          ⌘K
        </kbd>
      </button>

      {/* Mobile: icon only — the kbd hint is meaningless without a keyboard. */}
      <button
        type="button"
        onClick={onOpenPalette}
        aria-label="Search"
        className="sm:hidden p-2 rounded-lg text-surface-500 hover:bg-surface-100 dark:text-surface-400 dark:hover:bg-surface-800 transition-colors"
      >
        <MagnifyingGlassIcon className="h-5 w-5" aria-hidden="true" />
      </button>

      <Link
        to="/docs/user-guide"
        aria-label="Help"
        className="p-2 rounded-lg text-surface-500 hover:bg-surface-100 dark:text-surface-400 dark:hover:bg-surface-800 transition-colors"
      >
        <QuestionMarkCircleIcon className="h-5 w-5" aria-hidden="true" />
      </Link>

      <button
        type="button"
        onClick={cycleMode}
        aria-label={`Theme: ${mode}. Click to change.`}
        title={`Theme: ${mode}`}
        className="p-2 rounded-lg text-surface-500 hover:bg-surface-100 dark:text-surface-400 dark:hover:bg-surface-800 transition-colors"
      >
        <ThemeIcon className="h-5 w-5" aria-hidden="true" />
      </button>

      <button
        type="button"
        aria-label="Notifications"
        className="p-2 rounded-lg text-surface-500 hover:bg-surface-100 dark:text-surface-400 dark:hover:bg-surface-800 transition-colors"
      >
        <BellIcon className="h-5 w-5" aria-hidden="true" />
      </button>

      <Menu as="div" className="relative">
        <Menu.Button className="flex items-center gap-2 p-1.5 rounded-lg hover:bg-surface-100 dark:hover:bg-surface-800 transition-colors">
          <UserCircleIcon
            className="h-6 w-6 text-surface-500 dark:text-surface-400"
            aria-hidden="true"
          />
          {/*
            The Bedrock-registered display name is hidden in break-glass sessions — there is no
            Bedrock identity behind it to show.
          */}
          {!isLocalAdmin && user?.full_name ? (
            <span className="hidden sm:block text-sm font-medium text-surface-700 dark:text-surface-200 max-w-[12rem] truncate">
              {user.full_name}
            </span>
          ) : (
            <span className="hidden sm:block text-sm font-medium text-surface-700 dark:text-surface-200">
              {isLocalAdmin ? 'Local admin' : 'Account'}
            </span>
          )}
        </Menu.Button>

        <Transition
          as={Fragment}
          enter="transition ease-out duration-200"
          enterFrom="opacity-0 -translate-y-1"
          enterTo="opacity-100 translate-y-0"
          leave="transition ease-in duration-150"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <Menu.Items className="absolute right-0 mt-2 w-56 origin-top-right rounded-xl bg-white dark:bg-surface-800 border border-surface-200 dark:border-surface-700 shadow-modal p-1 focus:outline-none">
            <div className="px-3 py-2 border-b border-surface-200 dark:border-surface-700">
              <p className="text-sm font-medium text-surface-900 dark:text-surface-100 truncate">
                {isLocalAdmin ? 'Break-glass session' : (user?.full_name ?? 'Signed in')}
              </p>
              {!isLocalAdmin && user?.email ? (
                // Deliberately plain text, not a mailto — this is the signed-in user's own
                // address in a menu header, not a contact to click through to.
                <p className="text-xs text-surface-500 dark:text-surface-400 truncate">
                  {user.email /* lint-ok: raw-contact — own identity label, not a contact link */}
                </p>
              ) : null}
            </div>

            <Menu.Item>
              {({ active }) => (
                <button
                  type="button"
                  onClick={() => void logout()}
                  className={`w-full flex items-center gap-2 px-3 py-2 rounded-lg text-sm text-left transition-colors ${
                    active
                      ? 'bg-surface-100 dark:bg-surface-700 text-surface-900 dark:text-surface-100'
                      : 'text-surface-700 dark:text-surface-200'
                  }`}
                >
                  <ArrowRightOnRectangleIcon className="h-4 w-4" aria-hidden="true" />
                  Sign out
                </button>
              )}
            </Menu.Item>
          </Menu.Items>
        </Transition>
      </Menu>
    </header>
  );
}
